import { Box, Card, Group, rem, Skeleton, Table, Title } from '@mantine/core';
import classNames from 'classnames/bind';
import type { ReactNode } from 'react';
import { times } from 'remeda';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import titleStyleIdentifiers from './CustomTableTitle/CustomTableTitle.module.scss';
import styleIdentifiers from './customTable.module.scss';

const styles = classNames.bind(styleIdentifiers);
const titleStyles = classNames.bind(titleStyleIdentifiers);

const TAB_COUNT = 3;
const COLUMN_COUNT = 7;
const ROW_COUNT = 8;
const ROW_MIN_HEIGHT = rem(48);

type Props = {
  title: string;
  actions?: ReactNode;
  className?: string;
};

export function ViewsSkeleton({ title, actions, className }: Props) {
  const isMobile = useIsMobile();

  return (
    <div
      className={styles('CustomTable', 'views', className)}
      aria-busy="true"
      data-testid="views-skeleton"
    >
      <div className={titleStyles('custom-table-title', 'table-view')}>
        <div className={titleStyles('tab-container')}>
          <div className={titleStyles('title')}>
            <Title mr="space3" mt="space1" order={4}>
              {title}
            </Title>
          </div>
          <div className={titleStyles('tab-wrapper')}>
            {times(TAB_COUNT, tabIndex => (
              <div key={tabIndex} className={titleStyles('tab-item')}>
                <Skeleton h={rem(12)} w={rem(64)} radius="md" />
              </div>
            ))}
            <div className={titleStyles('border-mobile')} />
          </div>
        </div>
        <div className={titleStyles('actions-container', isMobile && 'space-between')}>
          <div className={titleStyles('actions')}>{actions}</div>
        </div>
      </div>
      <div className={styles('filters')}>
        {!isMobile && (
          <div className={styles('quick-search')}>
            <Skeleton h={rem(36)} w={rem(240)} radius="sm" />
          </div>
        )}
        <div className={styles('filters-actions')}>
          <Skeleton h={rem(30)} w={rem(110)} radius="sm" />
          <Skeleton h={rem(30)} w={rem(90)} radius="sm" />
        </div>
      </div>
      <Card
        radius={8}
        p={0}
        withBorder
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <Box style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Table stickyHeader horizontalSpacing="md" verticalSpacing="xs" fz="xs">
            <Table.Thead bg="purple.1">
              <Table.Tr>
                {times(COLUMN_COUNT, columnIndex => (
                  <Table.Th key={columnIndex} style={{ verticalAlign: 'middle' }}>
                    <Skeleton h={rem(12)} my={rem(8)} radius="md" />
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {times(ROW_COUNT, rowIndex => (
                <Table.Tr key={rowIndex} style={{ height: ROW_MIN_HEIGHT }}>
                  {times(COLUMN_COUNT, columnIndex => (
                    <Table.Td key={columnIndex}>
                      <Skeleton h={rem(25)} my={rem(5)} radius="md" />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
        <Group
          justify="space-between"
          p="sm"
          style={theme => ({ borderTop: `1px solid ${theme.colors.gray[2]}` })}
        >
          <Skeleton h={rem(20)} w={rem(180)} radius="sm" />
          <Skeleton h={rem(28)} w={rem(220)} radius="sm" />
        </Group>
      </Card>
    </div>
  );
}
